import { Box } from '@mui/material'
import { GridColDef, GridRenderCellParams, GridRenderHeaderFilterProps } from '@mui/x-data-grid-pro'
import React, { useMemo } from 'react'

import { FeedbackGridApi } from '../api/feedbackGridApi'
import { AnnotatedAction } from '../components/actions/AnnotatedAction'
import { CheckedAction } from '../components/actions/CheckedAction'
import { MultiSelectHeaderFilter } from '../components/filters/MultiSelectHeaderFilter'
import { TextHeaderFilter } from '../components/filters/TextHeaderFilter'
import { MermaidDiagram } from '../components/MermaidDiagram'
import { SentimentDisplay } from '../components/SentimentDisplay'
import {
	FeedbackGridColumn,
	SystemColumn,
	FeedbackManagementFilterType,
	FeedbackRow,
	FilterOption,
} from '../types/feedbackGrid.types'
import { getColumnWidth, getSystemColumnWidth } from '../utils/columnUtils'

interface UseGridColumnsProps {
	columns: FeedbackGridColumn[]
	systemColumns: SystemColumn[]
	widthUnit: number
	translations: Record<string, string>
	filterOptions: Record<string, FilterOption[]>
	api: FeedbackGridApi
	onEditCategories: (feedbackId: number) => void
	isUserCategoryManager: boolean
	TextFilter: React.ElementType
	MultiSelectFilter: React.ElementType
}

export const useGridColumns = ({
	columns,
	systemColumns,
	widthUnit,
	translations,
	filterOptions,
	api,
	onEditCategories,
	isUserCategoryManager,
	TextFilter,
	MultiSelectFilter,
}: UseGridColumnsProps) => {
	return useMemo(() => {
		const cols: GridColDef[] = []
		const isPercentMode = widthUnit === 1

		// Add data columns
		columns.forEach((col) => {
			const calculatedWidth = getColumnWidth(col.width, col.sortable, widthUnit)

			const colDef: GridColDef = {
				field: col.normalizedColumn,
				headerName: col.aliasColumn,
				sortable: col.sortable,
				filterable: col.filterType !== FeedbackManagementFilterType.NONE,
				renderCell: (params: GridRenderCellParams) => {
					const value = params.row.translations?.[col.normalizedColumn] || params.value
					return (
						<Box
							sx={{
								whiteSpace: 'normal',
								wordWrap: 'break-word',
								lineHeight: '1.5',
								py: 1,
								width: '100%',
							}}
						>
							{value}
						</Box>
					)
				},
			}

			if (col.filterType === FeedbackManagementFilterType.TEXT) {
				colDef.type = 'string'
				colDef.renderHeaderFilter = TextFilter
			} else if (col.filterType === FeedbackManagementFilterType.MULTI_SELECT) {
				colDef.type = 'singleSelect'
				colDef.valueOptions = filterOptions[col.normalizedColumn] || []
				colDef.renderHeaderFilter = MultiSelectFilter
			}

			if (isPercentMode) {
				colDef.flex = calculatedWidth
				colDef.minWidth = 100
			} else {
				colDef.width = calculatedWidth
			}

			cols.push(colDef)
		})

		// Add system columns
		systemColumns.forEach((col) => {
			if (!col.show) {
				return
			}

			const field = typeof col.column === 'string' ? col.column.replace('feedbacks.', '') : col.translation
			const calculatedWidth = getSystemColumnWidth(col.translation, widthUnit)

			const colDef: GridColDef = {
				field,
				headerName: translations[col.translation] || col.translation,
				sortable: col.sortable,
				filterable: col.showFilter && col.filter_identificator !== null,
				renderCell: (params: GridRenderCellParams) => {
					switch (col.column) {
						case 'feedbacks.checked':
							return (
								<CheckedAction
									feedbackId={params.row.id}
									initialValue={params.row.checked || false}
									api={api}
									translations={translations}
								/>
							)
						case 'feedbacks.annotated':
							return (
								<AnnotatedAction
									feedbackId={params.row.id}
									initialValue={params.row.annotated || false}
									api={api}
									translations={translations}
								/>
							)
						case 'ta_sentiment':
							return (
								<SentimentDisplay
									value={params.row.ta_sentiment}
									color={params.row.ta_sentiment_color || '#A7A7A7'}
									label={params.row.ta_sentiment_label || translations.neutral || 'Neutral'}
								/>
							)
						case 'ta_categories':
							return (
								<MermaidDiagram
									config={params.row.ta_categories_data?.config || { theme: 'base' }}
									graphDefinition={params.row.ta_categories_data?.graphDefinition || null}
									categories={params.row.ta_categories_data?.categories || []}
									feedbackId={params.row.id}
									editAllowed={params.row.ta_processed_flag !== null && isUserCategoryManager}
									onEditClick={() => onEditCategories(params.row.id)}
									translations={translations}
								/>
							)
						default: {
							const value = params.row[field]
							return (
								<Box
									sx={{
										whiteSpace: 'normal',
										wordWrap: 'break-word',
										lineHeight: '1.5',
										py: 1,
										width: '100%',
									}}
								>
									{value}
								</Box>
							)
						}
					}
				},
			}

			if (colDef.filterable && col.filter_identificator) {
				colDef.type = 'singleSelect'
				colDef.valueOptions = filterOptions[col.filter_identificator] || []
				colDef.renderHeaderFilter = MultiSelectFilter
			}

			if (isPercentMode) {
				colDef.flex = calculatedWidth
				colDef.minWidth = col.column === 'ta_categories' ? 450 : 100
			} else {
				colDef.width = Math.max(calculatedWidth, col.column === 'ta_categories' ? 450 : 0)
			}

			cols.push(colDef)
		})

		return cols
	}, [columns, systemColumns, widthUnit, translations, filterOptions, api, onEditCategories])
}
